import React, { useEffect, useState } from "react";
import { MessageCircle, QrCode, Save, Smartphone, UploadCloud } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API, { getMediaUrl } from "../services/api";

const defaultForm = {
  deposit_mode: "qr",
  deposit_qr_url: "/qr.png",
  deposit_upi_id: "",
  whatsapp_number: "",
  min_deposit: "300",
};

function AdminPaymentGateway() {
  const [form, setForm] = useState(defaultForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/admin/settings")
      .then((res) => setForm((current) => ({ ...current, ...(res.data || {}) })))
      .catch(() => {});
  }, []);

  const save = async (nextForm = form) => {
    setSaving(true);
    try {
      await API.post("/admin/settings", {
        deposit_mode: nextForm.deposit_mode || "qr",
        deposit_qr_url: nextForm.deposit_qr_url || "/qr.png",
        deposit_upi_id: nextForm.deposit_upi_id || "",
        whatsapp_number: nextForm.whatsapp_number || "",
        min_deposit: nextForm.min_deposit || "300",
      });
      alert("Payment gateway saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save payment gateway");
    } finally {
      setSaving(false);
    }
  };

  const setMode = (mode) => {
    const next = { ...form, deposit_mode: mode };
    setForm(next);
    save(next);
  };

  const uploadQr = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("qr", file);
    setUploading(true);
    try {
      const res = await API.post("/admin/payment-gateway/upload-qr", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((current) => ({ ...current, deposit_mode: "qr", deposit_qr_url: res.data?.url || current.deposit_qr_url }));
      alert("QR updated");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to upload QR");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Payment Gateway</h1>
            <p style={subtitle}>Choose how users deposit money and update QR directly.</p>
          </div>
          <button type="button" style={saveButton} onClick={() => save()} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>

        <section style={modeGrid}>
          <ModeCard
            active={form.deposit_mode === "qr"}
            icon={<QrCode size={24} />}
            title="QR Payment"
            text="Users scan your uploaded QR and submit payment proof."
            onClick={() => setMode("qr")}
          />
          <ModeCard
            active={form.deposit_mode === "intent"}
            icon={<Smartphone size={24} />}
            title="Intent Payment"
            text="Users open UPI apps directly with your UPI ID."
            onClick={() => setMode("intent")}
          />
          <ModeCard
            active={form.deposit_mode === "whatsapp"}
            icon={<MessageCircle size={24} />}
            title="WhatsApp Payment"
            text="Users contact WhatsApp for payment instructions."
            onClick={() => setMode("whatsapp")}
          />
        </section>

        <section style={grid}>
          <div style={card}>
            <h3 style={cardTitle}>QR Code</h3>
            <div style={qrPreview}>
              <img src={getMediaUrl(form.deposit_qr_url || "/qr.png")} alt="Deposit QR" style={qrImage} />
            </div>
            <label style={uploadButton}>
              <UploadCloud size={18} />
              {uploading ? "Uploading..." : "Upload New QR"}
              <input type="file" accept="image/*" onChange={uploadQr} style={hiddenFile} disabled={uploading} />
            </label>
            <p style={hint}>Upload clear square QR image. Recommended: 800 x 800 PNG/JPG.</p>
          </div>

          <div style={card}>
            <h3 style={cardTitle}>Payment Details</h3>
            <Field label="UPI ID for Intent Payment" value={form.deposit_upi_id} onChange={(value) => setForm((c) => ({ ...c, deposit_upi_id: value }))} />
            <Field label="WhatsApp Payment Number" value={form.whatsapp_number} onChange={(value) => setForm((c) => ({ ...c, whatsapp_number: value }))} />
            <Field label="Minimum Deposit" value={form.min_deposit} onChange={(value) => setForm((c) => ({ ...c, min_deposit: value }))} />
            <div style={activeBox}>
              <span>Current mode</span>
              <strong>{modeLabel(form.deposit_mode)}</strong>
            </div>
          </div>
        </section>
      </section>
    </AdminLayout>
  );
}

function ModeCard({ active, icon, title, text, onClick }) {
  return (
    <button type="button" style={{ ...modeCard, ...(active ? modeCardActive : null) }} onClick={onClick}>
      <span style={{ ...modeIcon, ...(active ? modeIconActive : null) }}>{icon}</span>
      <strong>{title}</strong>
      <small>{text}</small>
    </button>
  );
}

function Field({ label, value, onChange }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <input style={fieldInput} value={value || ""} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function modeLabel(mode) {
  if (mode === "intent") return "Intent Payment";
  if (mode === "whatsapp") return "WhatsApp Payment";
  return "QR Payment";
}

const page = { display: "grid", gap: "18px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "14px", flexWrap: "wrap" };
const title = { margin: 0, color: "#0f172a", fontSize: "26px", fontWeight: 900 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "14px" };
const saveButton = { minHeight: "46px", padding: "0 18px", border: 0, borderRadius: "14px", background: "#2563eb", color: "#fff", fontWeight: 900, display: "flex", alignItems: "center", gap: "8px" };
const modeGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "14px" };
const modeCard = { minHeight: "148px", border: "1px solid #e2e8f0", borderRadius: "22px", background: "#fff", padding: "18px", display: "grid", justifyItems: "start", gap: "10px", textAlign: "left", color: "#0f172a", boxShadow: "0 10px 24px rgba(15,23,42,.05)" };
const modeCardActive = { borderColor: "#93c5fd", background: "linear-gradient(135deg,#eff6ff,#ffffff)", boxShadow: "0 16px 34px rgba(37,99,235,.12)" };
const modeIcon = { width: "48px", height: "48px", borderRadius: "16px", background: "#f1f5f9", color: "#475569", display: "grid", placeItems: "center" };
const modeIconActive = { background: "#2563eb", color: "#fff" };
const grid = { display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: "18px" };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "24px", padding: "20px", boxShadow: "0 10px 24px rgba(15,23,42,.05)" };
const cardTitle = { margin: "0 0 14px", fontSize: "18px", fontWeight: 900, color: "#0f172a" };
const qrPreview = { width: "min(280px, 100%)", aspectRatio: "1 / 1", borderRadius: "22px", border: "1px dashed #93c5fd", background: "#f8fafc", display: "grid", placeItems: "center", overflow: "hidden" };
const qrImage = { width: "100%", height: "100%", objectFit: "contain", background: "#fff" };
const uploadButton = { marginTop: "14px", minHeight: "48px", width: "min(280px, 100%)", borderRadius: "16px", background: "#16a34a", color: "#fff", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "pointer" };
const hiddenFile = { display: "none" };
const hint = { color: "#64748b", fontSize: "12px", margin: "10px 0 0" };
const fieldWrap = { display: "grid", gap: "7px", marginBottom: "14px" };
const fieldLabel = { color: "#475569", fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em" };
const fieldInput = { height: "48px", border: "1px solid #dbe4f0", borderRadius: "14px", padding: "0 14px", fontSize: "14px", fontWeight: 700, outline: 0 };
const activeBox = { marginTop: "6px", borderRadius: "18px", padding: "16px", background: "#eff6ff", color: "#1d4ed8", display: "flex", justifyContent: "space-between", gap: "12px", fontWeight: 800 };

export default AdminPaymentGateway;
